import React, { useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination } from 'swiper/modules';
import 'swiper/swiper-bundle.css';
import { ISliderVanillaProps } from './types';
import { CarouselSlideLeft, CarouselSlideRight, CarouselWrapper } from './styles';
import { Swiper as SwiperType } from 'swiper/types';
import clsx from 'clsx';

export const Slider = ({
  slidesPerView = 1,
  isFullWidth = false,
  isStaticArrows = false,
  enableDots = true,
  className = '',
  isOverflowHidden = true,
  centeredSlides,
  isHiddenOnMobile = false,
  isAutoplayEnabled = false,
  loop = false,
  children,
}: ISliderVanillaProps) => {
  const swiperRef = useRef<SwiperType | null>(null);
  const [swiperState, setSwiperState] = useState({ isBeginning: true, isEnd: false });

  const handlePrevArrowClick = () => {
    if (swiperRef.current && !swiperRef.current.isBeginning) {
      swiperRef.current.slidePrev();
    }
  };

  const handleNextArrowClick = () => {
    if (swiperRef.current && !swiperRef.current.isEnd) {
      swiperRef.current.slideNext();
    }
  };

  const shouldBeFullWidth = isFullWidth && (swiperRef.current?.slides.length ?? 0) > 1;

  const handleSwiperUpdate = (swiper: SwiperType) => {
    setSwiperState({
      isBeginning: swiper.isBeginning,
      isEnd: swiper.isEnd,
    });
  };

  return (
    <CarouselWrapper
      data-test="carousel-wrapper"
      className={className}
      style={{ width: shouldBeFullWidth ? '100%' : undefined }}
    >
      {isStaticArrows && !swiperState.isBeginning && (
        <CarouselSlideLeft onClick={handlePrevArrowClick} />
      )}
      <Swiper
        modules={[Pagination, Autoplay]}
        onSwiper={(swiper: SwiperType) => {
          swiperRef.current = swiper;
          handleSwiperUpdate(swiper);
        }}
        onSlideChange={handleSwiperUpdate}
        spaceBetween={20}
        slidesPerView={swiperRef.current?.slides.length === 1 ? 1 : slidesPerView}
        slidesPerGroup={1}
        threshold={30}
        loop={loop}
        autoplay={isAutoplayEnabled ? { delay: 2500, disableOnInteraction: false } : false}
        centeredSlides={centeredSlides}
        pagination={{
          el: '.swiper-pagination',
          type: 'bullets',
          bulletClass: 'swiper-pagination-bullet',
          bulletActiveClass: 'swiper-pagination-bullet-active',
          clickable: enableDots,
        }}
        speed={300}
        breakpoints={{
          320: { slidesPerView: 1, spaceBetween: 24, centeredSlides: false },
          480: { slidesPerView: 1, spaceBetween: 24, centeredSlides: false },
          768: {
            slidesPerView,
            spaceBetween: 20,
            centeredSlides: children.length === 1,
          },
          1024: { slidesPerView, spaceBetween: 20, centeredSlides },
        }}
        className={className}
      >
        {React.Children.map(children, (child, index) => (
          <SwiperSlide
            key={index}
            className="relative"
            style={{ width: shouldBeFullWidth ? '100%' : undefined }}
          >
            {child}
          </SwiperSlide>
        ))}
      </Swiper>
      <div
        className={clsx('absolute flex gap-2 justify-center w-full swiper-pagination', {
          'hidden md:block': isHiddenOnMobile,
          'pb-4 sm:pb-20 md:pb-6': !isHiddenOnMobile,
        })}
      ></div>
      {isStaticArrows && !swiperState.isEnd && (
        <CarouselSlideRight onClick={handleNextArrowClick} />
      )}
    </CarouselWrapper>
  );
};
